import { ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LicenseInfo } from '@mui/x-license'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRoot } from 'react-dom/client'
import TagManager from 'react-gtm-module'
import 'react-quill-new/dist/quill.snow.css'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { tenMinutes } from '~constants/constants'
import ErrorPage from '~pages/error-page'
import { Cookie } from './components/cookie-banner/cookie-banner'
import { getStoredCookies } from './components/cookie-banner/utils/get-stored-cookies'
import AnimatedAlertProvider from './components/molecules/animated-alert/animated-alert-provider'
import {
  MswToggle,
  useMock,
} from './components/msw-toggle/sub-components/msw-toggle'
import { RefreshAfterVersionChange } from './components/refresh-after-version-change'
import './index.css'
import { worker } from './mocks/browser'
import { routeWrapper as routes } from './routes/routes'
import InsiteDigitalTheme from './theme/index'
import { applyEnvironmentIndication } from './utils/environment-indication'

applyEnvironmentIndication()

LicenseInfo.setLicenseKey(
  'bcb1a68df015df1c494038f93e00f4b2Tz0xMzI3OTQsRT0xODIyNjA3OTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9NCxBVD1tdWx0aSxLVj0y',
)

if (import.meta.hot) {
  import.meta.hot.accept()
}

if (import.meta.env.MODE === 'production') {
  Sentry.init({
    dsn: 'https://99ce3e56658c4205b529ac4aedd8aeea@o4505107599851520.ingest.sentry.io/4505107605815297',
    integrations: [
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: 'production',
  })
}

if (import.meta.env.DEV && useMock) {
  worker.start({
    onUnhandledRequest: 'bypass',
  })
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: tenMinutes,
      retry: false,
    },
  },
})

export const tagManagerArgs = {
  gtmId: 'GTM-MM44XLX', // defined in https://maersktankers.atlassian.net/browse/PPW-3065
}

if (getStoredCookies().includes(Cookie.statistical)) {
  TagManager.initialize(tagManagerArgs)
}

const router = createBrowserRouter(routes)

declare global {
  interface Window {
    disabledevtools: () => void
  }
}

window.disabledevtools = () => {
  localStorage.setItem('disableDevtools', 'true')
  window.location.reload()
}

createRoot(document.getElementById('root') as HTMLElement).render(
  <ThemeProvider theme={InsiteDigitalTheme}>
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AnimatedAlertProvider>
          <RefreshAfterVersionChange />
          <Sentry.ErrorBoundary fallback={<ErrorPage />}>
            <RouterProvider router={router} />
            {localStorage.getItem('disableDevtools') !== 'true' && (
              <>
                <MswToggle />
                <ReactQueryDevtools initialIsOpen={false} />
              </>
            )}
          </Sentry.ErrorBoundary>
        </AnimatedAlertProvider>
      </LocalizationProvider>
    </QueryClientProvider>
  </ThemeProvider>,
)
